<template>
  <div
    id="previewContainer"
    class="flex grow flex-col overflow-x-hidden overflow-y-auto pt-15"
    :class="getIsSmallLaptop ? '' : 'max-h-screen'"
  >
    <UiCard class="mx-4 flex flex-col gap-4 p-5">
      <div>
        <label>{{ $t('PREVIEW.DATE') }}</label>
        <DateTimePickers
          :value="previewFakeDate.toISOString()"
          @update:modelValue="setPreviewFakeDate"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label>{{ $t('PREVIEW.NAVIGATION') }}</label>
        <button class="btn btn-secondary btn-sm" @click="resyncPreview">
          <i class="fas fa-location-arrow" />
          {{ $t('PREVIEW.RESYNC', { page: resyncTargetName }) }}
        </button>
      </div>
    </UiCard>
    <ThreePositionSwitch
      leftLabel="XS"
      centerLabel="Medium"
      rightLabel="Large"
      @change="setAspectRatio"
    />
    <div
      class="relative left-4 -mb-24 flex w-100 shrink-0 origin-top scale-75 pt-2 pr-[0.6rem] pb-[0.3rem] pl-[0.7rem]"
      :style="{ height: platformHeight }"
    >
      <div
        id="wrapper"
        class="light relative flex h-full w-full flex-col overflow-hidden rounded-[2.5rem] shadow-[0px_0px_32px_-5px_#000000]"
      >
        <web-mobile-preview
          v-if="previewEverHadData"
          id="preview"
          ref="mobilePreview"
          class="block h-full"
          :page="JSON.stringify(pageIdentifier)"
          :packed-data="JSON.stringify(packedData)"
          :environment="JSON.stringify(mobilePreviewEnvironment)"
          :fake-time="previewFakeDate.unix() * 1000"
          :style="'background-color: ' + backgroundColor"
          @page-change="onPageChange"
        />
        <slot v-if="!previewEverHadData && showContent" name="content" />
        <div
          v-if="!previewEverHadData && !showContent"
          id="loading-screen"
          class="flex h-full w-full items-center justify-center bg-[#aaaaaa]"
        >
          <span>{{ $t('PREVIEW.STILL_LOADING') }}</span>
        </div>
      </div>
      <div
        class="pointer-events-none absolute top-[1.8rem] left-12 text-lg font-bold"
      >
        {{ previewFakeDate.format('HH:mm') }}
      </div>
      <div class="pointer-events-none absolute inset-0 flex flex-col">
        <img
          id="phone-top"
          alt=""
          class="h-auto w-full"
          src="@/assets/phone_frame_top.webp"
        />
        <img
          id="phone-middle"
          alt=""
          class="h-[calc(100%-1900px)] w-full grow"
          src="@/assets/phone_frame_center.webp"
        />
        <img
          id="phone-bottom"
          alt=""
          class="h-auto w-full"
          src="@/assets/phone_frame_bottom.webp"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import '@code-troopers/mobile-preview'
import dayjs from 'dayjs'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import DateTimePickers from '@/pages/festival/components/DateTimePickers.vue'
import {
  DeeplinkElementType,
  isDeeplinkElementType,
} from '@/pages/menuEntries/MenuEntry.type'
import UiCard from '@/shared/components/ui/UiCard.vue'
import { ExternalScriptsService } from '@/shared/service/externalScriptsService'
import { FontsService } from '@/shared/service/fontsService'
import { colorService } from '@/shared/service/utils.service'
import { useIsMobileStore } from '@/shared/store/isMobile.store'
import {
  type PreviewPageData,
  usePreviewStore,
} from '@/shared/store/preview.store'
import { type PreviewGrabData } from '@/shared/types/applicationData.type'

import ThreePositionSwitch, {
  type SwitchPosition,
} from '../ThreePositionSwitch.vue'

const { t, te } = useI18n()

defineOptions({ name: 'PreviewComponent' })

const props = withDefaults(
  defineProps<{
    page: DeeplinkElementType
    showContent?: boolean
    pageData?: PreviewPageData | null
    packedData?: PreviewGrabData | null
    defaultControls?: string[]
  }>(),
  {
    showContent: false,
    pageData: null,
    packedData: null,
    defaultControls: undefined,
  },
)

const mobilePreview = ref<(HTMLElement & { forceResync: () => void }) | null>(
  null,
)
const mobilePreviewEnvironment =
  ExternalScriptsService.getMobilePreviewEnvironment()
const previewFakeDate = ref(dayjs())
const aspectRatio = ref(9 / 16)
const previewEverHadData = ref(false)

const _isMobileStore = useIsMobileStore()
const getIsSmallLaptop = computed(() => _isMobileStore.isSmallLaptop)

const previewStore = usePreviewStore()
const resyncToHomeState = computed(() => previewStore.resyncToHome)
const resyncPageState = computed(() => previewStore.resyncPage)
const resyncPageDataState = computed(() => previewStore.resyncPageData)
const pageState = computed(() => previewStore.page)

const pageDataState = computed(() => previewStore.pageData)
const platformHeight = computed(() => {
  const WIDTH_REM = 25
  return `${WIDTH_REM / aspectRatio.value}rem`
})

const pageIdentifier = computed(() => {
  return {
    ...props.pageData,
    params: props.pageData,
    type: props.page,
  }
})

const backgroundColor = computed(() => {
  return colorService.argb_to_rgba(
    props.packedData?.applicationConfig.colorBackground ?? '',
  )
})

const resyncTargetName = computed(() => {
  const resyncToHome = resyncToHomeState.value
  const resyncPage = resyncPageState.value
  const { page } = props
  if (resyncToHome) return t('DEEPLINK_COMPONENT.LABEL.HOME')

  const name = resyncPage ?? page

  // Mapping of backoffice page names to mobile app i18n keys
  const mapping: Record<string, string> = {
    [DeeplinkElementType.EVENT]: 'SHOW',
    [DeeplinkElementType.GUEST]: 'SHOWMAN',
    [DeeplinkElementType.PRACTICAL_INFO]: 'INFO',
    [DeeplinkElementType.PRACTICAL_INFO_LIST]: 'PRACTICALINFOS',
    [DeeplinkElementType.SPONSOR_LIST]: 'SPONSORS',
    [DeeplinkElementType.HOME]: 'HOME',
    [DeeplinkElementType.PROGRAM]: 'PROGRAMMATION',
    [DeeplinkElementType.NOTIFICATIONS]: 'NOTIFICATIONS',
    [DeeplinkElementType.LIKES]: 'LIKES',
    [DeeplinkElementType.MENU]: 'MENU',
    [DeeplinkElementType.CHAT]: 'CHAT',
    [DeeplinkElementType.MY_TICKETS]: 'MY_TICKETS',
    [DeeplinkElementType.GUEST_LIST]: 'GUEST_LIST',
    [DeeplinkElementType.GUEST_LIST_TYPE]: 'GUEST_LIST_TYPE',
    [DeeplinkElementType.WEEZEVENT]: 'WEEZEVENT',
    [DeeplinkElementType.SPLASH]: 'SPLASH',
    [DeeplinkElementType.MAP]: 'MAP',
    [DeeplinkElementType.GALLERY]: 'GALLERY',
    [DeeplinkElementType.PROGRAM_TAGS_FILTER]: 'PROGRAM_TAGS_FILTER',
    [DeeplinkElementType.SAFER]: 'SAFER',
    [DeeplinkElementType.VIDEOS]: 'VIDEOS',
    [DeeplinkElementType.ABOUT]: 'ABOUT',
    [DeeplinkElementType.TICKETING]: 'TICKETING',
    [DeeplinkElementType.CASHLESS]: 'CASHLESS',
    [DeeplinkElementType.EMPTY]: 'EMPTY',
  }

  const key = mapping[name] || name.toUpperCase()
  const i18nKey = `DEEPLINK_COMPONENT.LABEL.${key}`

  return te(i18nKey) ? t(i18nKey) : name
})

watch(
  () => props.packedData,
  (data: PreviewGrabData | null) => {
    if (data && !previewEverHadData.value) {
      previewEverHadData.value = true
    }
  },
  { immediate: true },
)

onMounted(async () => {
  await FontsService.createPreviewStylesFromAppConfig()
})

function resyncPreview() {
  const { resyncToHome, resyncPage, resyncPageData, page, pageData } = {
    resyncToHome: resyncToHomeState.value,
    resyncPage: resyncPageState.value,
    resyncPageData: resyncPageDataState.value,
    page: pageState.value,
    pageData: pageDataState.value,
  }

  // Determine the target: if resyncToHome is active (pages without sync), go to Home.
  // Otherwise, use the explicit resync target, or fall back to the current page.
  const targetPage = resyncToHome
    ? DeeplinkElementType.HOME
    : (resyncPage ?? page ?? DeeplinkElementType.HOME)
  const targetData = resyncToHome ? null : (resyncPageData ?? pageData)

  // Force update the store to ensure attributes are correct
  previewStore.setPreview({
    page: targetPage,
    pageData: targetData,
  })

  // Wait for Vue to update DOM attributes, then force React to reload
  void nextTick(() => {
    if (
      mobilePreview.value &&
      typeof mobilePreview.value.forceResync === 'function'
    ) {
      mobilePreview.value.forceResync()
    }
  })
}

function setPreviewFakeDate(dateAsISOString: string): void {
  previewFakeDate.value = dayjs(dateAsISOString)
}

function setAspectRatio(position: SwitchPosition): void {
  switch (position) {
    case 'left':
      aspectRatio.value = 3 / 4
      break
    case 'center':
      aspectRatio.value = 9 / 16
      break
    case 'right':
      aspectRatio.value = 9 / 19.5
      break
  }
}

function onPageChange(event: Event) {
  if (!(event instanceof CustomEvent)) return
  const detail = event.detail as unknown
  if (detail == null || typeof detail !== 'object') return
  const detailRecord = detail as Record<string, unknown>
  if (typeof detailRecord.type !== 'string') return
  if (!isDeeplinkElementType(detailRecord.type)) return

  const pageData =
    'params' in detailRecord &&
    typeof detailRecord.params === 'object' &&
    detailRecord.params != null
      ? (detailRecord.params as Record<string, unknown>)
      : null

  previewStore.setPreview({
    page: detailRecord.type,
    pageData,
  })
}
</script>

<style scoped>
/* ::-webkit-scrollbar is a pseudo-element — not expressible as a Tailwind utility. */
#wrapper {
  ::-webkit-scrollbar {
    display: none;
  }
}
</style>

<style>
web-mobile-preview div {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

web-mobile-preview div::-webkit-scrollbar {
  display: none;
}
</style>
